/**
 * VictoryModal — game-over overlay with animated score and breakdown.
 *
 * Features a gradient accent stripe at the top of the card that
 * matches performance tier, mono-spaced score counter, and
 * detailed scoring breakdown.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Performance tiers (move-count based)
// ---------------------------------------------------------------------------

function accentClass(expansions) {
  if (expansions <= 5)  return 'exceptional';
  if (expansions <= 12) return 'excellent';
  if (expansions <= 20) return 'great';
  return 'over';
}

// ---------------------------------------------------------------------------
// Animated counter hook
// ---------------------------------------------------------------------------

function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    const start = performance.now();
    const from = 0;

    function tick(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (progress < 1) raf.current = requestAnimationFrame(tick);
    }

    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);

  return value;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function VictoryModal({ scoreData, expansions, onNewGame, onViewGraph, targetWord, initialWord }) {
  const { score, baseScore, quickWinBonus, difficultyMultiplier, difficultyBonus, rating } = scoreData;
  const accent = accentClass(expansions);
  const animatedScore = useCountUp(score);
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(() => {
    const start = (initialWord || '').replace(/\b\w/g, c => c.toUpperCase());
    const target = (targetWord || '').replace(/\b\w/g, c => c.toUpperCase());
    const stars = score >= 900 ? '\u2b50\u2b50\u2b50' : score >= 700 ? '\u2b50\u2b50' : score >= 400 ? '\u2b50' : '';
    const text = `LexiQuest: ${start} \u2192 ${target} in ${expansions} moves\n${score} pts ${stars} (${rating})\nhttps://lexiquest.app`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [score, expansions, rating, targetWord, initialWord]);

  return (
    <div className="modal-overlay">
      <div className={`modal accent-${accent}`}>
        <div className="modal-title">Quest Complete</div>
        <div className={`modal-rating ${accent}`}>{rating}</div>

        <div className="modal-score-display">
          <div className="modal-score-num">{animatedScore}</div>
          <span className="modal-score-unit">points</span>
        </div>

        {/* Breakdown */}
        <div className="breakdown">
          <div className="breakdown-row">
            <span className="breakdown-key">Base Score</span>
            <span className="breakdown-val">{baseScore}</span>
          </div>
          {quickWinBonus > 0 && (
            <div className="breakdown-row">
              <span className="breakdown-key">Quick Win Bonus</span>
              <span className="breakdown-val bonus">+{quickWinBonus}</span>
            </div>
          )}
          {difficultyMultiplier && difficultyMultiplier !== 1.0 && (
            <div className="breakdown-row">
              <span className="breakdown-key">Difficulty ({difficultyMultiplier}x)</span>
              <span className={`breakdown-val ${difficultyBonus >= 0 ? 'bonus' : 'penalty'}`}>
                {difficultyBonus >= 0 ? '+' : ''}{difficultyBonus}
              </span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="modal-stats">
          <div className="modal-stat">
            <div className="modal-stat-val">{expansions}</div>
            <div className="modal-stat-lbl">Moves</div>
          </div>
          <div className="modal-stat">
            <div className={`modal-stat-val ${accent}`}>{rating}</div>
            <div className="modal-stat-lbl">Rating</div>
          </div>
        </div>

        <div className="modal-buttons">
          <button className="btn-new" onClick={onNewGame}>
            Play Again
          </button>
          <button className="btn-share" onClick={handleShare}>
            {copied ? 'Copied!' : 'Share Result'}
          </button>
          <button className="btn-view-graph" onClick={onViewGraph}>
            View Graph
          </button>
        </div>
      </div>
    </div>
  );
}
